import React, { useState } from 'react';
import { Input } from './input';
import { ScrollArea } from './scroll-area';
import { Search } from 'lucide-react';

/**
 * SearchableSelect - A searchable dropdown component
 * 
 * @param {Array} items - Array of items to display
 * @param {string} value - Currently selected value
 * @param {function} onValueChange - Callback when selection changes
 * @param {function} getItemValue - Function to extract value from item (default: item => item.id || item._id)
 * @param {function} getItemLabel - Function to extract label from item (default: item => item.name)
 * @param {string} placeholder - Placeholder text
 * @param {string} searchPlaceholder - Search input placeholder
 * @param {string} emptyMessage - Message when no items found
 */
export default function SearchableSelect({
  items = [],
  value,
  onValueChange,
  getItemValue = (item) => item.id || item._id,
  getItemLabel = (item) => item.name,
  placeholder = "Select an item...",
  searchPlaceholder = "Search...",
  emptyMessage = "No items found.",
  disabled = false
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  // Filter items based on search term
  const filteredItems = items.filter(item => {
    const label = getItemLabel(item).toLowerCase();
    return label.includes(searchTerm.toLowerCase());
  });

  // Get the label of currently selected item
  const selectedItem = items.find(item => getItemValue(item) === value);
  const selectedLabel = selectedItem ? getItemLabel(selectedItem) : placeholder;

  const handleSelect = (item) => {
    onValueChange(getItemValue(item));
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="relative w-full">
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          flex h-10 w-full items-center justify-between rounded-md border border-input 
          bg-background px-3 py-2 text-sm ring-offset-background 
          placeholder:text-muted-foreground 
          focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 
          disabled:cursor-not-allowed disabled:opacity-50
          ${!selectedItem && 'text-muted-foreground'}
        `}
      >
        <span className="truncate">{selectedLabel}</span>
        <svg
          className={`h-4 w-4 opacity-50 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown Content */}
          <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover text-popover-foreground shadow-md animate-in fade-in-80">
            {/* Search Input */}
            <div className="flex items-center border-b px-3 py-2">
              <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
              <Input
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-8 border-0 p-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                autoFocus
              />
            </div>

            {/* Items List */}
            <ScrollArea className="max-h-[300px]">
              {filteredItems.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  {emptyMessage}
                </div>
              ) : (
                <div className="p-1">
                  {filteredItems.map((item) => {
                    const itemValue = getItemValue(item);
                    const isSelected = itemValue === value;
                    
                    return (
                      <button
                        key={itemValue}
                        type="button"
                        onClick={() => handleSelect(item)}
                        className={`
                          relative flex w-full cursor-pointer select-none items-center 
                          rounded-sm px-2 py-1.5 text-sm outline-none 
                          transition-colors
                          hover:bg-accent hover:text-accent-foreground
                          ${isSelected ? 'bg-accent text-accent-foreground font-medium' : ''}
                        `}
                      >
                        <span className="truncate">{getItemLabel(item)}</span>
                        {isSelected && (
                          <svg
                            className="ml-auto h-4 w-4"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </div>
        </>
      )}
    </div>
  );
}
